<template>
  <b-card class="wallet-table-card">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div>
        <h5 class="mb-0">
          Monederos
        </h5>
        <div class="text-muted small">
          Clientes con saldo y su balance actual
        </div>
      </div>
      <b-button
        size="sm"
        variant="outline-primary"
        @click="$emit('refresh')"
      >
        <b-spinner
          v-if="loading"
          small
          class="mr-1"
        />
        Refrescar
      </b-button>
    </div>

    <div class="d-flex flex-wrap align-items-end mb-1">
      <b-form-input
        v-model="filters.q"
        size="sm"
        class="mr-1 mb-1"
        placeholder="Buscar cliente, correo o wallet id…"
      />
      <b-button
        size="sm"
        variant="outline-secondary"
        class="mb-1"
        @click="resetFilters"
      >
        Limpiar
      </b-button>
      <div class="text-muted small ml-auto mb-1">
        Mostrando {{ filteredItems.length }} de {{ displayTotal }}
      </div>
    </div>

    <b-table
      :items="filteredItems"
      :fields="fields"
      :busy="loading"
      small
      hover
      responsive
      head-variant="light"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :empty-text="loading ? 'Cargando…' : 'Sin monederos en el resumen'"
    >
      <template #cell(customer)="ctx">
        <router-link
          v-if="ctx.item.customer_id"
          class="wallet-link"
          :to="{ name: 'WalletForCustomer', params: { customerId: ctx.item.customer_id } }"
        >
          <div class="font-weight-bold">
            {{ ctx.item.customer_name || '—' }}
          </div>
          <div class="text-muted small">
            {{ ctx.item.customer_email || '—' }}
          </div>
        </router-link>
        <div v-else>
          <div class="font-weight-bold">
            {{ ctx.item.customer_name || '—' }}
          </div>
          <div class="text-muted small">
            {{ ctx.item.customer_email || '—' }}
          </div>
        </div>
      </template>

      <template #cell(balance)="ctx">
        <div class="text-success font-weight-bold">
          {{ money(ctx.item.balance) }}
        </div>
      </template>

      <template #cell(wallet_id)="ctx">
        <code class="wallet-id">{{ ctx.item.wallet_id || '—' }}</code>
      </template>
    </b-table>

    <div class="d-flex justify-content-end">
      <b-pagination
        v-if="pagination.total_objects > pagination.per_page"
        :value="pagination.page"
        :per-page="pagination.per_page"
        :total-rows="pagination.total_objects"
        size="sm"
        class="mt-2"
        @change="$emit('change-page', $event)"
      />
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'WalletAdminWalletsTable',
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    pagination: {
      type: Object,
      default: () => ({ page: 1, per_page: 25, total_objects: 0 }),
    },
  },
  data() {
    return {
      sortBy: 'balance',
      sortDesc: true,
      filters: { q: '' },
      fields: [
        {
          key: 'customer',
          label: 'Cliente',
          thClass: 'align-middle',
        },
        {
          key: 'balance',
          label: 'Saldo',
          sortable: true,
          thClass: 'text-right align-middle',
          tdClass: 'text-right',
        },
        {
          key: 'wallet_id',
          label: 'Wallet ID',
          sortable: true,
          thClass: 'align-middle',
        },
      ],
    }
  },
  computed: {
    displayTotal() {
      return this.pagination && this.pagination.total_objects ? this.pagination.total_objects : this.normalizedItems.length
    },
    normalizedItems() {
      const items = Array.isArray(this.items) ? this.items : []
      return items.map(w => {
        const customer = w.customer || {}
        const customerId = w.customer_id || w.customerId || customer.id || null
        const customerName = w.customer_name || w.customerName || customer.name || w.name || '—'
        const customerEmail = w.customer_email || w.customerEmail || customer.email || w.email || '—'
        const walletId = w.wallet_id || w.walletId || w.id || '—'

        const balance = this.pickBalance(w)

        return {
          customer_id: customerId,
          customer_name: customerName,
          customer_email: customerEmail,
          wallet_id: walletId,
          balance,
          raw: w,
        }
      })
    },
    filteredItems() {
      const q = String(this.filters.q || '').trim().toLowerCase()
      if (!q) return this.normalizedItems

      return this.normalizedItems.filter(w => {
        const hay = [
          w.customer_name,
          w.customer_email,
          w.wallet_id,
          w.customer_id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return hay.includes(q)
      })
    },
  },
  methods: {
    pickBalance(w) {
      const candidates = [
        w.balance,
        w.wallet_balance,
        w.total_balance,
        w.amount,
        w.current_balance,
      ]
      for (let i = 0; i < candidates.length; i += 1) {
        const v = candidates[i]
        if (v !== undefined && v !== null) return Number(v)
      }
      return 0
    },
    resetFilters() {
      this.filters = { q: '' }
    },
    money(v) {
      const n = Number(v || 0)
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n)
    },
  },
}
</script>

<style scoped>
.wallet-link {
  color: inherit;
  text-decoration: none;
}

.wallet-link:hover .font-weight-bold {
  text-decoration: underline;
}

.wallet-table-card {
  border: 1px solid #edf0f4;
  box-shadow: 0 10px 24px rgba(17, 38, 63, 0.06);
}

.wallet-table-card ::v-deep .table thead th {
  background: #f8fafc;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: #8a94a6;
  border-bottom: 1px solid #e9eef3;
}

.wallet-table-card ::v-deep .table tbody tr:hover {
  background: #f3f6fb;
}

.wallet-id {
  font-size: 0.8rem;
  color: #6c757d;
  background: #f6f8fa;
  padding: 2px 6px;
  border-radius: 6px;
}
</style>
